import React, { useEffect, useLayoutEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useAbility } from '@casl/react';
import jwt from 'jwt-decode';
import NavigationProvider from './app/components/NavigationProvider';
import routes from './app/config/routes';
import Alert from './app/components/Utils/Alert';
import GlobalErrorBoundary from './app/screens/Error/GlobalErrorBoundary';
import { getSettings, sendAlert } from './app/actions/utils';
import { AbilityContext } from './app/config/abilityContext';
import { updateAbility } from './app/config/ability';
import { requestSignOut } from './app/actions/auth';
import {
  getLocalizedUrl,
  parseLocalizedUrl,
  getBaseRoute
} from './app/config/route-translations';
import './app/config/i18n';
import API_CONFIG from './app/config/configurations';

const App = () => {
  const { signedIn } = useSelector(state => state.auth);
  const dispatch = useDispatch();

  // Before any child (e.g. Page) runs: Comapa modal script reads this for POST /leads.
  useLayoutEffect(() => {
    if (typeof window !== 'undefined') {
      window.NAVIMAG_API_BASE = API_CONFIG.url();
    }
  }, []);

  const ability = useAbility(AbilityContext);

  const handleOnLogin = () => {
    if (!signedIn) return;
    updateAbility(ability);
  };

  const handleTrailingSlashRedirect = () => {
    const { pathname, search } = window.location;

    if (pathname === '/' || !pathname.endsWith('/')) {
      return;
    }

    const newPathname = pathname.slice(0, -1);
    const newUrl = newPathname + search;

    // Use window.history.replaceState for redirect handling
    window.history.replaceState(null, '', newUrl);
  };

  const validLanguage = language => {
    const validLanguages = ['es', 'en'];
    return validLanguages.includes(language.toLowerCase());
  };

  const setInitialLanguage = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const languageParam = urlParams.get('language');
    const urlPath = window.location.pathname;
    const { language: pathLanguage, route: pathRoute } = parseLocalizedUrl(
      urlPath
    );

    // Skip redirects for admin, auth, cargo, and special routes
    const skipRedirectPaths = [
      '/admin',
      '/login',
      '/register',
      '/reset_password',
      '/recover_password',
      '/cargo',
      '/form/',
      '/home'
    ];
    const shouldSkipRedirect = skipRedirectPaths.some(path =>
      urlPath.startsWith(path)
    );

    // Priority 1: Language from URL path (/es/ or /en/)
    if (pathLanguage && validLanguage(pathLanguage)) {
      localStorage.setItem('language', pathLanguage);
      // Clear dontOverride flag if we're on a valid language-prefixed URL
      localStorage.removeItem('dontOverride');
      return; // Already on correct language-prefixed URL
    }

    // Priority 2: Language from query parameter (?language=es)
    if (languageParam !== null && validLanguage(languageParam)) {
      const targetLanguage = languageParam.toLowerCase();
      localStorage.setItem('language', targetLanguage);

      // Handle root path with language param
      if (urlPath === '/' || urlPath === '') {
        if (targetLanguage === 'en') {
          const newSearch = window.location.search
            .replace(/[?&]language=[^&]*/, '')
            .replace(/^&/, '?');
          window.location.replace(`/en${newSearch}`);
        } else {
          // For Spanish, remove language param and stay on /
          const newSearch = window.location.search
            .replace(/[?&]language=[^&]*/, '')
            .replace(/^&/, '?');
          window.location.replace(`/${newSearch}`);
        }
        return;
      }

      // Redirect to language-prefixed URL
      const baseRoute = getBaseRoute(pathRoute, targetLanguage) || pathRoute;
      let localizedUrl;

      // Handle special cases
      if (urlPath === '/public/blogs' || urlPath.startsWith('/public/blogs')) {
        // Redirect to blog entry point
        localizedUrl =
          targetLanguage === 'es'
            ? '/es/blog-viajes-ferry-chile'
            : '/en/chile-ferry-travel-blog';
      } else if (
        urlPath.startsWith('/blog/es/') ||
        urlPath.startsWith('/blog/en/')
      ) {
        // Blog posts - maintain /blog/es/slug and /blog/en/slug structure
        const slug = urlPath.replace(/^\/blog\/(es|en)\//, '');
        localizedUrl = `/blog/${targetLanguage}/${slug}`;
      } else if (urlPath === '/blog/es' || urlPath === '/blog/en') {
        // Blog listing - redirect to entry point
        localizedUrl =
          targetLanguage === 'es'
            ? '/es/blog-viajes-ferry-chile'
            : '/en/chile-ferry-travel-blog';
      } else if (
        urlPath === '/es/blog-viajes-ferry-chile' ||
        urlPath === '/en/chile-ferry-travel-blog'
      ) {
        // Blog entry point - switch language
        localizedUrl =
          targetLanguage === 'es'
            ? '/es/blog-viajes-ferry-chile'
            : '/en/chile-ferry-travel-blog';
      } else if (urlPath === '/public/faqs') {
        localizedUrl =
          targetLanguage === 'es'
            ? '/es/preguntas-frecuentes-ferry-pasajeros'
            : '/en/passenger-ferry-faq';
      } else {
        localizedUrl = getLocalizedUrl(baseRoute, targetLanguage);
      }

      const newSearch = window.location.search
        .replace(/[?&]language=[^&]*/, '')
        .replace(/^&/, '?');
      window.location.replace(localizedUrl + newSearch);
      return;
    }

    // Priority 3: Detect language from path if it contains /es/ or /en/
    // Also handle /es and /en as standalone paths
    if (
      urlPath === '/es' ||
      (urlPath.includes('/es/') && languageParam === null)
    ) {
      localStorage.setItem('language', 'es');
      localStorage.removeItem('dontOverride');
      return; // Already on correct path
    }
    if (
      urlPath === '/en' ||
      (urlPath.includes('/en/') && languageParam === null)
    ) {
      localStorage.setItem('language', 'en');
      localStorage.removeItem('dontOverride');
      return; // Already on correct path
    }

    // Priority 4: Use stored language or default to Spanish
    if (!localStorage.getItem('language')) {
      localStorage.setItem('language', 'es');
    }

    // Handle root path (/) - always redirect to /es by default if no language is stored
    if (urlPath === '/' && !shouldSkipRedirect) {
      // Check if user has a language preference stored
      const storedLanguage = localStorage.getItem('language');

      // If user has English stored, redirect to /en
      if (storedLanguage === 'en') {
        const newSearch = window.location.search.replace(
          /[?&]language=[^&]*/,
          ''
        );
        window.location.replace(`/en${newSearch}`);
        return;
      }

      // Default: redirect to /es (as per requirements - no language config stored)
      const newSearch = window.location.search.replace(
        /[?&]language=[^&]*/,
        ''
      );
      window.location.replace(`/es${newSearch}`);
      return;
    }

    // Note: Old route redirects are now handled by LanguageRedirect component
    // This ensures redirects happen before React Router renders the component

    // Clear dontOverride flag after processing
    localStorage.removeItem('dontOverride');
  };

  useEffect(() => {
    handleTrailingSlashRedirect();
    setInitialLanguage();
  }, []);
  useEffect(handleOnLogin, [signedIn]);

  useEffect(() => {
    const payload = localStorage.getItem('jwt');

    const runSettings = () => dispatch(getSettings());
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(runSettings, { timeout: 2500 });
    } else {
      window.setTimeout(runSettings, 0);
    }

    if (!payload) return;
    const currentTime = Date.now() / 1000;
    const isExpired = currentTime >= jwt(payload).exp;

    if (isExpired) {
      dispatch(sendAlert({ kind: 'error', message: 'Tu sesión ha expirado' }));
      dispatch(requestSignOut());
    }
  }, []);

  return (
    <GlobalErrorBoundary>
      <BrowserRouter>
        <NavigationProvider>{routes}</NavigationProvider>
      </BrowserRouter>
      <Alert />
    </GlobalErrorBoundary>
  );
};

const mapStateToProps = state => {
  const { utils } = state.utils;
  return {
    utils
  };
};

export default connect(mapStateToProps)(App);
